/*
 * AdminVendorBilling — iter-214
 * --------------------------------
 * Admin-side billing overview. Replica of the vendor-side Settings →
 * Billing tab, but with a multi-vendor switcher, search, and
 * consolidated summary across All / Active / Trial vendors.
 *
 * Invoice generation is intentionally deferred — Zoho Books integration
 * will own PDF rendering once connected.
 */
import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { CreditCard, Users, Clock, CheckCircle2, Search, FileText, IndianRupee, AlertTriangle, Download, Eye, RefreshCw } from 'lucide-react';
import PlanManagementPanel from '../../components/vendor/PlanManagementPanel';
import SubscriptionActionsPanel from '../../components/admin/SubscriptionActionsPanel';
import TrialGateFunnelPanel from '../../components/admin/TrialGateFunnelPanel';
import RazorpayRouteDiagnoseCard from '../../components/admin/RazorpayRouteDiagnoseCard';
import { toast } from '../../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatINR = (v) =>
  (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
};

const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-green-100 text-green-800 border-green-200',
    trial: 'bg-amber-100 text-amber-800 border-amber-200',
    none: 'bg-gray-100 text-gray-600 border-gray-200',
    cancelled: 'bg-red-100 text-red-800 border-red-200',
    paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  };
  return (
    <Badge className={`${map[status] || map.none} border capitalize`} data-testid={`billing-status-${status}`}>
      {status || 'none'}
    </Badge>
  );
};

const AdminVendorBilling = () => {
  const [scope, setScope] = useState('all');
  const [search, setSearch] = useState('');
  const [vendors, setVendors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [listError, setListError] = useState('');

  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token')}`,
    }),
    []
  );

  const fetchList = async () => {
    setLoadingList(true);
    setListError('');
    try {
      const qs = new URLSearchParams({ scope });
      if (search) qs.set('q', search);
      const [vRes, sRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/vendor-billing/vendors?${qs}`, { headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/admin/vendor-billing/summary?scope=${scope}`, { headers: authHeaders }),
      ]);
      if (vRes.ok) {
        const vd = await vRes.json();
        setVendors(vd.vendors || []);
      } else {
        // iter-301 — previously a non-200 response silently left the list
        // empty with zero explanation ("no vendors" looked identical to a
        // real empty result). Surface the actual failure now.
        const errBody = await vRes.text().catch(() => '');
        setVendors([]);
        setListError(`Failed to load vendors (HTTP ${vRes.status}). ${errBody.slice(0, 200)}`);
      }
      if (sRes.ok) setSummary(await sRes.json());
    } catch (e) {
      setVendors([]);
      setListError(`Failed to load vendors: ${e?.message || 'network error'}`);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchDetail = async (id) => {
    if (!id) { setDetail(null); return; }
    setLoadingDetail(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/vendor-billing/vendor/${id}`, {
        headers: authHeaders,
      });
      if (res.ok) setDetail(await res.json());
      else setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  // iter-307d — "No invoices yet" could mean genuinely never billed, OR
  // it could mean Razorpay webhooks were missed/disabled for a stretch
  // (confirmed production case: a vendor with 2 successful recurring
  // Razorpay payments and 0 local invoices). This pulls the vendor's
  // COMPLETE subscription invoice history directly from Razorpay's own
  // Invoice API and backfills any paid cycle missing locally —
  // idempotent, safe to click more than once.
  const [syncingFromRazorpay, setSyncingFromRazorpay] = useState(false);
  const syncFromRazorpay = async (id) => {
    setSyncingFromRazorpay(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/admin/commission-invoices/audit/subscription-backfill-deep?vendor_id=${id}&dry_run=false`,
        { method: 'POST', headers: authHeaders }
      );
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      if (data.generated > 0) {
        toast.success(`Synced ${data.generated} invoice(s) from Razorpay for this vendor.`);
      } else if (data.failed > 0) {
        // iter-307d — distinguish "we couldn't even reach Razorpay for
        // this subscription ID" (test/seed fixture, unlikely on a real
        // production vendor) from a genuine invoice-generation failure
        // (e.g. missing GST/billing state) — the latter is the one an
        // admin should actually worry about.
        const lookupFailed = (data.results || []).some((r) => r.status === 'razorpay_lookup_failed');
        toast.error(
          lookupFailed
            ? 'Could not reach Razorpay for this vendor\'s subscription ID — verify it is a real, active subscription.'
            : `Sync found ${data.failed} paid invoice(s) it could not generate — check backend logs for details.`
        );
      } else {
        toast.info('No new paid invoices found on Razorpay for this vendor.');
      }
      await fetchDetail(id);
    } catch (e) {
      toast.error(e?.message || 'Sync from Razorpay failed');
    } finally {
      setSyncingFromRazorpay(false);
    }
  };

  // iter-303 — Invoice History rows had NO view/download action at all
  // (backend route + frontend button never existed for subscription
  // invoices, unlike commission invoices). Uses the same authenticated
  // fetch + blob: URL pattern as the commission-invoice pages (a bare
  // `<a href>`/`window.open` can't carry the Bearer token).
  const previewInvoice = async (invoiceNumber) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/vendor-billing/invoices/${encodeURIComponent(invoiceNumber)}/html`,
        { headers: authHeaders }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to load invoice preview');
    }
  };

  const downloadInvoice = async (invoiceNumber) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/vendor-billing/invoices/${encodeURIComponent(invoiceNumber)}/pdf`,
        { headers: authHeaders }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber.replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to download invoice');
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [scope]);
  useEffect(() => { fetchDetail(selectedId); /* eslint-disable-next-line */ }, [selectedId]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(fetchList, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [search]);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="admin-vendor-billing-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <CreditCard className="h-6 w-6" /> Vendor Billing
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Admin view — consolidated and per-vendor billing history. Invoice PDFs will be
              generated via Zoho Books once the integration is live.
            </p>
          </div>
        </div>

        {/* iter-301 — surface fetch failures instead of a silent empty list */}
        {listError && (
          <div
            className="flex items-start gap-2 bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg"
            data-testid="billing-list-error-banner"
          >
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-red-900">Couldn't load vendors</p>
              <p className="text-sm text-red-800">{listError}</p>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="billing-summary-cards">            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">MRR</p>
                    <p className="text-2xl font-bold text-indigo-600" data-testid="billing-mrr">
                      {formatINR(summary.mrr)}
                    </p>
                  </div>
                  <IndianRupee className="h-8 w-8 text-indigo-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">ARR</p>
                    <p className="text-2xl font-bold text-purple-600" data-testid="billing-arr">
                      {formatINR(summary.arr)}
                    </p>
                  </div>
                  <IndianRupee className="h-8 w-8 text-purple-200" />
                </div>
              </CardContent>
            </Card>
            {/* iter-301c — Lifetime Charged card. Backend /summary already
                computed this correctly; it just had no card to render into
                (only visible per-vendor before this). */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Lifetime Charged</p>
                    <p className="text-2xl font-bold text-emerald-600" data-testid="billing-lifetime-charged">
                      {formatINR(summary.lifetime_charged)}
                    </p>
                  </div>
                  <IndianRupee className="h-8 w-8 text-emerald-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Active</p>
                    <p className="text-2xl font-bold text-green-600" data-testid="billing-active-count">
                      {summary.active_count}
                    </p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">On Trial</p>
                    <p className="text-2xl font-bold text-amber-600" data-testid="billing-trial-count">
                      {summary.trial_count}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* iter-221f — Trial Gate Funnel (read-only) */}
        <TrialGateFunnelPanel />

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by email, business name, or vendor name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="billing-search-input"
                />
              </div>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="w-full md:w-48" data-testid="billing-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vendors</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="trial">On Trial Only</SelectItem>
                  <SelectItem value="none">No Subscription</SelectItem>
                </SelectContent>
              </Select>
              <Select value={selectedId} onValueChange={setSelectedId}>
                <SelectTrigger className="w-full md:w-64" data-testid="billing-vendor-select">
                  <SelectValue placeholder="— Select a vendor —" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.business_name || v.email} {v.status === 'trial' ? '(trial)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Vendor List */}
        {!selectedId && (
          <Card data-testid="billing-vendor-list">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5" /> {loadingList ? 'Loading…' : `${vendors.length} vendors`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((v) => (
                    <TableRow key={v.id} data-testid={`billing-row-${v.id}`}>
                      <TableCell className="font-medium">{v.business_name || '—'}</TableCell>
                      <TableCell className="text-sm text-gray-600">{v.email}</TableCell>
                      <TableCell className="capitalize">{v.plan_code || '—'}</TableCell>
                      <TableCell className="capitalize">{v.billing_cycle || '—'}</TableCell>
                      <TableCell><StatusBadge status={v.status} /></TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedId(v.id)}
                          data-testid={`billing-view-${v.id}`}
                        >
                          View Billing
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Per-vendor dossier */}
        {selectedId && (
          <Card data-testid="billing-detail-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {detail?.business_name || detail?.email || 'Vendor billing'}
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => setSelectedId('')}>
                  ← Back to list
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingDetail && <p className="text-gray-500">Loading…</p>}
              {detail && (
                <>
                  {detail.is_trial && (
                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg" data-testid="billing-detail-trial-banner">
                      <p className="font-semibold text-amber-900">Trial — ₹0 charged</p>
                      <p className="text-sm text-amber-800">
                        Trial ends {formatDate(detail.trial_end)}. Billing will start the day after.
                      </p>
                    </div>
                  )}

                  {/* iter-303 — Reordered into tabs (Invoices first/default)
                      per user feedback: "too many things before we get to
                      the invoice section". Plan override / subscription
                      actions / Razorpay diagnostics moved to a second tab. */}
                  <Tabs defaultValue="invoices" className="w-full">
                    <TabsList data-testid="billing-detail-tabs">
                      <TabsTrigger value="invoices" data-testid="billing-tab-invoices">
                        Plan &amp; Invoices
                      </TabsTrigger>
                      <TabsTrigger value="diagnostics" data-testid="billing-tab-diagnostics">
                        Plan Override &amp; Diagnostics
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="invoices" className="space-y-6 mt-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="bg-slate-50">
                          <CardContent className="pt-4">
                            <p className="text-xs uppercase text-gray-500">Current Plan</p>
                            <p className="text-lg font-semibold" data-testid="billing-detail-plan-name">
                              {detail.plan?.name || '—'}
                            </p>
                            <div className="text-sm text-gray-600 mt-2 space-y-1">
                              <p>Monthly: {formatINR(detail.plan?.monthly_price)}</p>
                              <p>Annual: {formatINR(detail.plan?.annual_price)}</p>
                              <p>Commission: {detail.plan?.commission_percentage || '—'}%</p>
                            </div>
                          </CardContent>
                        </Card>
                        <Card className="bg-slate-50">
                          <CardContent className="pt-4">
                            <p className="text-xs uppercase text-gray-500">Current Period</p>
                            <p className="text-sm font-medium">
                              {formatDate(detail.current_period?.start)}
                              <br />→ {formatDate(detail.current_period?.end)}
                            </p>
                            <p className="text-xs text-gray-500 mt-2 capitalize">
                              {detail.billing_cycle} cycle · {detail.status}
                            </p>
                          </CardContent>
                        </Card>
                        <Card className="bg-slate-50">
                          <CardContent className="pt-4">
                            <p className="text-xs uppercase text-gray-500">Upcoming Bill</p>
                            <p className="text-2xl font-bold text-indigo-700" data-testid="billing-detail-upcoming">
                              {formatINR(detail.upcoming_bill?.total)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Due {formatDate(detail.upcoming_bill?.due_on)} · {detail.upcoming_bill?.note}
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-base font-semibold flex items-center gap-2">
                            <FileText className="h-4 w-4" /> Invoice History
                          </h3>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            disabled={syncingFromRazorpay}
                            onClick={() => syncFromRazorpay(selectedId)}
                            data-testid="billing-sync-from-razorpay-btn"
                          >
                            <RefreshCw className={`h-3.5 w-3.5 ${syncingFromRazorpay ? 'animate-spin' : ''}`} />
                            {syncingFromRazorpay ? 'Syncing...' : 'Sync from Razorpay'}
                          </Button>
                        </div>
                        {detail.invoices && detail.invoices.length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Invoice #</TableHead>
                                <TableHead>Period</TableHead>
                                <TableHead>Base</TableHead>
                                <TableHead>GST</TableHead>
                                <TableHead>Total</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {detail.invoices.map((inv) => (
                                <TableRow key={inv.id} data-testid={`sub-invoice-row-${inv.id}`}>
                                  <TableCell className="font-mono text-xs">{inv.invoice_number || inv.id}</TableCell>
                                  <TableCell className="text-xs">
                                    {formatDate(inv.period_start)} → {formatDate(inv.period_end)}
                                  </TableCell>
                                  <TableCell>
                                    {formatINR(inv.base_amount)}
                                    {inv.lead_charges && inv.lead_charges.total > 0 && (
                                      <div
                                        className="text-[10px] text-amber-700 mt-0.5 whitespace-nowrap"
                                        data-testid={`sub-invoice-lead-charges-${inv.id}`}
                                        title={(inv.lead_charges.breakdown || [])
                                          .map((b) => `${b.category || 'General'}: ${b.count} lead(s)`)
                                          .join(', ')}
                                      >
                                        + Lead gen ({inv.lead_charges.lead_count}): {formatINR(inv.lead_charges.taxable)}
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell>{formatINR(inv.gst_amount)}</TableCell>
                                  <TableCell className="font-semibold">{formatINR(inv.total)}</TableCell>
                                  <TableCell>
                                    <StatusBadge status={inv.status} />
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2"
                                        disabled={!inv.invoice_number}
                                        onClick={() => previewInvoice(inv.invoice_number)}
                                        data-testid={`sub-invoice-view-${inv.id}`}
                                      >
                                        <Eye className="h-3.5 w-3.5" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2"
                                        disabled={!inv.invoice_number}
                                        onClick={() => downloadInvoice(inv.invoice_number)}
                                        data-testid={`sub-invoice-download-${inv.id}`}
                                      >
                                        <Download className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <p className="text-sm text-gray-500 italic" data-testid="billing-detail-no-invoices">
                            No invoices yet — this vendor has not been billed, or webhooks may have
                            missed a Razorpay charge. Click "Sync from Razorpay" above to check.
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-3">
                          Lifetime charged: <span className="font-semibold">{formatINR(detail.lifetime_charged)}</span>
                          {' · '}
                          {detail.lifetime_invoices} invoice{detail.lifetime_invoices === 1 ? '' : 's'}
                        </p>
                      </div>
                    </TabsContent>

                    <TabsContent value="diagnostics" className="space-y-6 mt-4">
                      {/* iter-221c — Plan Management (admin override) */}
                      <PlanManagementPanel
                        vendorId={selectedId}
                        onAction={() => fetchDetail(selectedId)}
                      />
                      {/* iter-230bw Phase 5 — Subscription Actions */}
                      <SubscriptionActionsPanel
                        vendorId={selectedId}
                        vendorEmail={detail.email}
                        onChanged={() => fetchDetail(selectedId)}
                      />
                      {/* iter-231-P — Razorpay Route Linked Account diagnostic */}
                      <RazorpayRouteDiagnoseCard vendorId={selectedId} />
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminVendorBilling;
